import React from "react";
import PropTypes from "prop-types";
import { Alert } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const EGroupWarning = ({ selectedGroup, canManageGroup, showSyncMessage }) => {
  const isReplicaGroup =
    selectedGroup.syncType === "SlaveWithPlaceholders" ||
    selectedGroup.syncType === "Slave";

  const validityLimitDate = selectedGroup.validityLimit
    ? new Date(selectedGroup.validityLimit)
    : null;
  const isValidityLimitValid =
    validityLimitDate instanceof Date &&
    !Number.isNaN(validityLimitDate.getTime());
  const now = new Date();
  const millisecondsInMonth = 30 * 24 * 60 * 60 * 1000;
  const isExpired =
    isValidityLimitValid && validityLimitDate.getTime() < now.getTime();
  const isExpiringSoon =
    isValidityLimitValid &&
    !isExpired &&
    validityLimitDate.getTime() - now.getTime() <= millisecondsInMonth;
  const syncStatusMessages = {
    NotSynced: "This group has not yet been synchronized.",
    Synchronized: "Group is correctly synchronized.",
    OwnerError:
      "This group has an owner problem, preventing synchronization. Please verify that a valid owner is assigned.",
    MetadataError:
      "This group has a metadata error, preventing synchronization.",
    DynamicError: "This group has a dynamic error, preventing synchronization.",
    GroupMembershipError:
      "This group has a group membership error, preventing synchronization.",
  };

  const getStatusType = () => {
    switch (selectedGroup.syncStatus) {
      case "NotSynced":
        return "warning";
      case "Synchronized":
        return "success";
      case "OwnerError":
      case "MetadataError":
      case "DynamicError":
      case "GroupMembershipError":
        return "error";
      default:
        return "warning";
    }
  };

  const renderSyncStatusMessage = () => (
    <span>{syncStatusMessages[selectedGroup.syncStatus]}</span>
  );

  const renderSubscribeMessage = () => (
    <>
      <span>
        This group is managed by Egroups. Please subscribe at{" "}
        <a
          href={`https://e-groups.cern.ch/e-groups/Egroup.do?egroupName=${selectedGroup.groupIdentifier}`}
        >
          https://e-groups.cern.ch
        </a>
        .
      </span>
      <br />
    </>
  );

  const renderSyncTypeMessage = () => (
    <>
      <span>
        You cannot modify this group because it is being synchronized from the{" "}
        <a
          href={`https://e-groups.cern.ch/e-groups/Egroup.do?egroupName=${selectedGroup.groupIdentifier}`}
        >
          legacy EGroups service
        </a>
        .
      </span>
      <br />
      <span>
        To make this group editable and migrate it to GMS, please check the
        Synchronization tab above.
      </span>
    </>
  );

  const renderExpiredGroupMessage = (message) => (
    <>
      <span>{message}</span>
    </>
  );

  const renderExpiringSoonMessage = () => (
    <>
      <span>This group is set to expire soon.</span>
    </>
  );

  // If can't manage, still display a warning and a link to Egroups
  return (
    <>
      {canManageGroup && (isReplicaGroup || showSyncMessage) && (
        <Alert
          type={showSyncMessage ? getStatusType() : "error"}
          showIcon
          icon={<ExclamationCircleOutlined style={{ marginRight: 16 }} />}
          message={
            showSyncMessage
              ? renderSyncStatusMessage()
              : renderSyncTypeMessage()
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {!canManageGroup && isReplicaGroup && (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined style={{ marginRight: 16 }} />}
          message={renderSubscribeMessage()}
          style={{ marginBottom: 16 }}
        />
      )}

      {isExpired && (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined style={{ marginRight: 16 }} />}
          message={renderExpiredGroupMessage(
            canManageGroup
              ? "You cannot modify this group because it has expired."
              : "This group has expired."
          )}
          style={{ marginBottom: 16 }}
        />
      )}

      {isExpiringSoon && (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined style={{ marginRight: 16 }} />}
          message={renderExpiringSoonMessage()}
          style={{ marginBottom: 16 }}
        />
      )}
    </>
  );
};

EGroupWarning.propTypes = {
  selectedGroup: PropTypes.object,
  canManageGroup: PropTypes.bool,
  showSyncMessage: PropTypes.bool,
};

export default EGroupWarning;
